import { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  TextField,
  Grid,
  Typography,
  CircularProgress,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import API_MIDDLEWARE from "../../redux/api/middleware";
import { APP_CUSTOM_ERROR } from "../../constants/AppVarConstants";
import { setErrorMessage, setSuccessMessage } from "../../redux/actions/appActions";
import { JOBS_LISTING_URL, JOBS_LISTING_URL_EMPLOYER, UPDATE_JOB_STATUS_URL } from "../../redux/api/config";
import InfiniteScroll from "react-infinite-scroll-component";
import { debounce } from "lodash";
import { ROUTE_CLIENT_JOB_DETAILS, ROUTE_CREATE_JOB } from "../../constants/routes";
import { useNavigate } from "react-router-dom";
import JobCard from "./JobCard";
import { getUserCompanyDetails } from "../../redux/actions/b2bActions";

const JobsDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.app.user);
  const { companyDetails } = useSelector((state: any) => state.b2b);
  const { companyName: userCompanyName, companyId } = user?.user || {};
  const companyName = userCompanyName || companyDetails?.name || '';
  const roles = localStorage.getItem("roles");
  const isJobUser = roles?.toUpperCase().includes('EMPLOYER_USER');
  const [hasMore, setHasMore] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [newJobCount, setNewJobCount] = useState(0);
  const [jobToUpdate, setJobToUpdate] = useState<any>(null);
  const [newStatus, setNewStatus] = useState<string>("ACTIVE");
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showActiveJobs, setShowActiveJobs] = useState(true);
  const fetchJobs = (flag: boolean, resetOffset: boolean, jobAction = false) => {
    if (!flag) {
      setLoading(true);
    }
    const filters: any = {
      status: showActiveJobs ? 1 : 2,
    };

    const payload = {
      searchQuery: searchText,
      companyId: companyId,
      page: jobAction ? currentPage : resetOffset ? 1 : currentPage + 1,
      fetchSize: 10,
      filters,
    };
    API_MIDDLEWARE(JOBS_LISTING_URL_EMPLOYER, "POST", { "Content-Type": "application/json" }, payload)
      .then((res: any) => {
        const { records, hasNext, page, totalRecords, newJobCount } = res?.result || {};
        const jobs: any = resetOffset ? [...records] : [...data, ...records];
        
        setData(jobs);
        setCurrentPage(page);
        setHasMore(hasNext);
        setLoading(false);
        setTotal(totalRecords);
        setNewJobCount(newJobCount || 0);
      })
      .catch((err: any) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        setLoading(false);
      });
  };

  const debouncedFetchJobs = debounce(() => fetchJobs(false, true), 500);

  useEffect(() => {
    debouncedFetchJobs();

    return debouncedFetchJobs.cancel;
  }, [searchText]);

  // Handle active/inactive filter changes
  useEffect(() => {
    // Reset existing data when status filter changes
    setData([]);
    setCurrentPage(1);
    setHasMore(false);
    setTotal(0);
    
    fetchJobs(false, true);
  }, [showActiveJobs]);

  useEffect(() => {
    fetchJobs(false, true);
    // Fetch company details if not already available (for company users)
    if (!companyDetails?.name && !userCompanyName) {
      dispatch(getUserCompanyDetails());
    }
  }, []);
  const handleEditClick = (jobId: string) => {
    navigate(`${ROUTE_CREATE_JOB}?edit=true&jobId=${jobId}`);
  };
  const handleViewClick = (jobId: string) => {
    navigate(`${ROUTE_CLIENT_JOB_DETAILS}/${jobId}`);
  };
  const handleStatusChangeClick = (jobId: string, newStatusValue: string) => {
    // JobCard already calculates the new status, so use it directly
    setNewStatus(newStatusValue);

    setJobToUpdate({ jobId, newStatus: newStatusValue });
    setStatusDialogOpen(true);
  };

  const handleStatusConfirm = async () => {
    if (!jobToUpdate?.jobId) return;
    setIsUpdatingStatus(true);
    try {
      // Prepare payload - include postedDate when activating
      const payload: any = {};
      if (newStatus === "ACTIVE") {
        // Set current date as postedDate when activating
        payload.postedDate = new Date().toISOString();
      }

      const res = await API_MIDDLEWARE(
        UPDATE_JOB_STATUS_URL(jobToUpdate.jobId, newStatus),
        "PUT",
        { "Content-Type": "application/json" },
        payload
      );

      dispatch(setSuccessMessage(`Job ${newStatus === "ACTIVE" ? 'activated' : 'deactivated'} successfully!`));
      
      // Remove job from current view if it no longer matches the filter
      // If viewing Active jobs and deactivating, or viewing Inactive jobs and activating, remove it
      const shouldRemove = (showActiveJobs && newStatus !== "ACTIVE") || (!showActiveJobs && newStatus === "ACTIVE");
      
      if (shouldRemove) {
        setData((prevData: any[]) =>
          prevData.filter((job: any) => job.jobId !== jobToUpdate.jobId)
        );
        setTotal((prevTotal) => Math.max((prevTotal || 0) - 1, 0));
      } else {
        // Update the job in place if it still matches the filter
        setData((prevData: any[]) =>
          prevData.map((job: any) =>
            job.jobId === jobToUpdate.jobId
              ? { 
                  ...job, 
                  status: newStatus,
                  // Update postedDate if activating
                  ...(newStatus === "ACTIVE" && { postedDate: payload.postedDate })
                }
              : job
          )
        );
      }

      // Close dialog
      setStatusDialogOpen(false);
      setJobToUpdate(null);

      return res?.result;
    } catch (err: any) {
      dispatch(setErrorMessage(err?.errorMsg ?? APP_CUSTOM_ERROR));
      return false;
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleStatusCancel = () => {
    setStatusDialogOpen(false);
    setJobToUpdate(null);
    setIsUpdatingStatus(false);
  };
  return (
    <Box
      sx={{
        backgroundColor: "#e5e4fe",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >

      <Box
        sx={{
          backgroundColor: "#e5e4fe",
          width: "100%",
          padding: "20px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left side content: "Hey Team Ogilvy!" */}
        <Box
          sx={{
            fontSize: "30px",
            fontWeight: "500",
            color: "#000",
            marginLeft: "32px",
          }}
        >
          Hey Team {companyName}!
        </Box>


        {/* Right side: Search button */}
        <Box
          sx={{
            marginRight: "32px",
            display: "flex",
            alignItems: "center",
            width: "359px",
            height: "45px",
            border: "1px solid #756DE9",
            borderRadius: "100px",
            position: "relative",
          }}
        >
          {/* Search input */}
          <TextField
            variant="standard"
            placeholder="Search..."
            sx={{
              width: "100%",
              paddingLeft: "15px",
              fontSize: "16px",
              "& .MuiInput-underline:before": { borderBottom: "none" },
              "& .MuiInput-underline:after": { borderBottom: "none" },
            }}
            InputProps={{
              disableUnderline: true,
            }}
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
          />

          {/* Search Icon */}
          <IconButton
            sx={{
              position: "absolute",
              right: "10px",
              padding: "0",
            }}
          >
            <Box
              component="img"
              src="/images/client-website/search-line.png"
              alt="search icon"
              sx={{
                width: "24px",
                height: "24px",
              }}
            />
          </IconButton>

          {/* Dropdown for recent searches */}
          {/* <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              style: {
                width: "315px",
                backgroundColor: "#fff",
                padding: "20px", // Custom padding
                borderRadius: "10px", // Rounded corners
                border: "1px solid #756DE9",
                marginTop: "10px",
              },
            }}
          >
            {searches.map((search, index) => (
              <MenuItem
                key={index}
                onClick={() => handleSelect(search)} // When an option is clicked, the value will be selected
                sx={{
                  fontSize: "16px",
                  fontWeight: "400",
                  color: "#444242",
                }}
              >
                {search}
              </MenuItem>
            ))}
          </Menu> */}
        </Box>
      </Box>
      <Box
        sx={{
          padding: { xs: "20px", sm: 4 },
          backgroundColor: "#fff",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          width: "100%",
        }}
      >
        <Box
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 4, marginBottom: "24px" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: "600" }}>
              {showActiveJobs ? "Active" : "Inactive"} Job Posts ({total})
            </Typography>
            {newJobCount > 0 && !isJobUser && (
              <Chip
                label={`${newJobCount} New`}
                sx={{
                  backgroundColor: "#4CAF50",
                  color: "#fff",
                  fontWeight: "600",
                  fontSize: "12px",
                  height: "28px",
                  borderRadius: "14px",
                  "& .MuiChip-label": {
                    px: 1.5,
                  },
                }}
              />
            )}
          </Box>
            
        {/* Center: Active/Inactive Toggle Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              backgroundColor: "#F5F5F5",
              borderRadius: "25px",
              padding: "4px",
              border: "1px solid #E0E0E0",
            }}
          >
            <Button
              onClick={() => setShowActiveJobs(true)}
              sx={{
                borderRadius: "20px",
                padding: "8px 24px",
                fontSize: "14px",
                fontWeight: "500",
                textTransform: "none",
                backgroundColor: showActiveJobs ? "#756DE9" : "transparent",
                color: showActiveJobs ? "#FFFFFF" : "#666666",
                minWidth: "100px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: showActiveJobs ? "#5a4dbf" : "#E8E8F0",
                  color: showActiveJobs ? "#FFFFFF" : "#333333",
                },
                boxShadow: showActiveJobs ? "0px 2px 8px rgba(117, 109, 233, 0.3)" : "none",
              }}
            >
              Active
            </Button>
            <Button
              onClick={() => setShowActiveJobs(false)}
              sx={{
                borderRadius: "20px",
                padding: "8px 24px",
                fontSize: "14px",
                fontWeight: "500",
                textTransform: "none",
                backgroundColor: !showActiveJobs ? "#756DE9" : "transparent",
                color: !showActiveJobs ? "#FFFFFF" : "#666666",
                minWidth: "100px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: !showActiveJobs ? "#5a4dbf" : "#E8E8F0",
                  color: !showActiveJobs ? "#FFFFFF" : "#333333",
                },
                boxShadow: !showActiveJobs ? "0px 2px 8px rgba(117, 109, 233, 0.3)" : "none",
              }}
            >
              Inactive
            </Button>
          </Box>
        </Box>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            sx={{
              backgroundColor: "#756DE9",
              color: "#fff",
              borderRadius: "100px",
              padding: "10px 20px",
              "&:hover": {
                backgroundColor: "#5a4dbf",
                color: "#fff",
                borderColor: "#5a4dbf",
              },
              textTransform: "capitalize",
            }}
            onClick={() => navigate(ROUTE_CREATE_JOB)}
          >
            Create New Job Post
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : data.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "60px 20px",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                backgroundColor: "#F5F5F5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "24px",
              }}
            >
              <Box
                component="img"
                src="/images/client-website/search-line.png"
                alt="No data"
                sx={{
                  width: "40px",
                  height: "40px",
                  opacity: 0.5,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#333333",
                marginBottom: "8px",
              }}
            >
              No {showActiveJobs ? "Active" : "Inactive"} Jobs Found
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                color: "#666666",
                marginBottom: "24px",
                maxWidth: "400px",
              }}
            >
              {showActiveJobs
                ? "There are currently no active job posts. Create a new job post to get started."
                : "There are currently no inactive job posts. Switch to active jobs to see available positions."
              }
            </Typography>
            {showActiveJobs && (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                sx={{
                  backgroundColor: "#756DE9",
                  color: "#fff",
                  borderRadius: "100px",
                  padding: "12px 24px",
                  "&:hover": {
                    backgroundColor: "#5a4dbf",
                  },
                  textTransform: "capitalize",
                }}
                onClick={() => navigate(ROUTE_CREATE_JOB)}
              >
                Create New Job Post
              </Button>
            )}
          </Box>
        ) : (
          <InfiniteScroll
            dataLength={data.length}
            next={() => fetchJobs(true, false)}
            hasMore={hasMore}
            loader={
              <Box component="h4" sx={{ padding: 4 }}>
                Loading...
              </Box>
            }
            endMessage={
              <Box component="p" sx={{ textAlign: "center", mt: 2 }}>
                <b></b>
              </Box>
            }
          >
            <Grid container spacing={2} px={4} justifyContent="flex-start">
              {data.map((el: any, idx: number) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <JobCard 
                    {...el} 
                    onEditClick={handleEditClick} 
                    onViewClick={handleViewClick}
                    showEditInHeader={true}
                    onStatusChange={handleStatusChangeClick}
                  />
                </Grid>
              ))}
            </Grid>
          </InfiniteScroll>
        )}
      </Box>

      {/* Status Change Confirmation Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleStatusCancel}
        aria-labelledby="status-dialog-title"
        aria-describedby="status-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 1,
          }
        }}
      >
        <DialogTitle
          id="status-dialog-title"
          sx={{
            color: newStatus === "ACTIVE" ? '#4CAF50' : '#f44336',
            fontWeight: 600,
            fontSize: '1.25rem',
            textAlign: 'center',
          }}
        >
          Confirm {newStatus === "ACTIVE" ? 'Activation' : 'Deactivation'}
        </DialogTitle>
        <DialogContent>
          <Typography
            id="status-dialog-description"
            sx={{
              textAlign: 'center',
              color: '#666',
              fontSize: '1rem',
              padding: '8px 0',
            }}
          >
            Are you sure you want to {newStatus === "ACTIVE" ? 'activate' : 'deactivate'} this job post?
            <br />
            {newStatus === "ACTIVE"
              ? 'This will make the job visible to candidates.'
              : 'This will hide the job from candidates.'
            }
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, padding: '16px 24px' }}>
          <Button
            onClick={handleStatusCancel}
            variant="outlined"
            disabled={isUpdatingStatus}
            sx={{
              borderColor: '#756DE9',
              color: '#756DE9',
              '&:hover': {
                borderColor: '#5a52d5',
                backgroundColor: 'rgba(117, 109, 233, 0.08)',
              },
              borderRadius: 2,
              paddingX: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStatusConfirm}
            variant="contained"
            disabled={isUpdatingStatus}
            sx={{
              backgroundColor: newStatus === "ACTIVE" ? '#4CAF50' : '#f44336',
              '&:hover': {
                backgroundColor: newStatus === "ACTIVE" ? '#45a049' : '#d32f2f',
              },
              borderRadius: 2,
              paddingX: 3,
              minWidth: 100,
            }}
          >
            {isUpdatingStatus ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                {newStatus === "ACTIVE" ? 'Activating...' : 'Deactivating...'}
              </Box>
            ) : (
              newStatus === "ACTIVE" ? 'Activate' : 'Deactivate'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobsDashboard;
